import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  type Auth,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

export interface FirebaseServices {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
  storage: FirebaseStorage;
}

let services: FirebaseServices | null = null;
let emulatorsConnected = false;

export function isDemoMode(): boolean {
  return import.meta.env.VITE_USE_DEMO_DATA === 'true';
}

export function getFirebaseServices(): FirebaseServices {
  if (services) return services;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
  const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;
  const databaseId = import.meta.env.VITE_FIRESTORE_DATABASE_ID ?? 'default';
  if (!projectId || !apiKey || !authDomain || !storageBucket || !appId) {
    throw new Error('Firebase configuration is missing. Enable demo data for local development.');
  }

  const app =
    getApps()[0] ??
    initializeApp({
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      appId,
    });
  const auth = getAuth(app);
  void setPersistence(auth, browserLocalPersistence);
  const db = getFirestore(app, databaseId);
  const functions = getFunctions(
    app,
    import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION ?? 'southamerica-east1',
  );
  const storage = getStorage(app);

  if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true' && !emulatorsConnected) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    emulatorsConnected = true;
  }

  const appCheckKey = import.meta.env.VITE_APP_CHECK_SITE_KEY;
  if (import.meta.env.PROD && appCheckKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  services = { app, auth, db, functions, storage };
  return services;
}
